import { P, Section as KitSection, H3, Checks, Nots, Warn, Ext } from "./IAArticleKit";

/* IndianAirports — OCI & NRI Rules article (owner-provided copy, Sept 2026).
   Rendered on the /india/about tab AND the standalone SEO page /india/guides/oci-nri-rules.
   Each section has its own Listen button (browser TTS) for long-form comfort. */

const Section = (props) => <KitSection tid="oci-listen" {...props} />;

const COMPARE = [
  ["Citizenship", "Indian citizen", "Foreign citizen"],
  ["Primary travel document", "Indian passport", "Foreign passport plus OCI/e-OCI"],
  ["Indian visa required", "No", "No separate visa ordinarily required while OCI status is valid"],
  ["Indian voting rights", "Eligible subject to election law", "Not eligible"],
  ["Indian passport", "Holds or may obtain one as an Indian citizen", "Cannot hold one unless the person becomes an Indian citizen"],
  ["Right to live in India", "Based on Indian citizenship", "Indefinite residence under OCI status, subject to law"],
  ["Agricultural property", "Subject to the laws applicable to Indian citizens and FEMA status", "Cannot ordinarily purchase agricultural land, plantation property or a farmhouse"],
  ["Government employment", "Subject to applicable eligibility rules", "Generally not entitled to public employment reserved for citizens"],
  ["Arrival card", "Indian-citizen travelers are not included in the foreigner/OCI e-Arrival requirement", "e-Arrival Card required under current official instructions"],
];

const NRI_TRAVEL = [
  "Does not require an Indian visa", "Does not require an OCI card",
  "Should use the Indian passport at Indian immigration",
  "Must comply with airline, customs, baggage, health and security requirements",
  "Should ensure the passport is valid for the journey and any connecting country",
  "Should carry required transit visas when traveling through another country",
  "May be asked for an onward or return itinerary by an airline or transit authority",
];

const OCI_IS_NOT = [
  "An Indian passport", "Dual citizenship", "Proof of Indian citizenship", "A right to vote",
  "A guarantee of government employment", "An exemption from Indian laws",
  "Permission to purchase every category of property", "Automatic Indian tax residency",
  "Automatic eligibility for every citizen-only benefit",
];

const ELIGIBLE = [
  "Was an Indian citizen at the time of, or after, the commencement of the Constitution",
  "Was eligible to become an Indian citizen when the Constitution commenced",
  "Belonged to a territory that later became part of India",
  "Is a child, grandchild or great-grandchild of an eligible person",
  "Is a qualifying minor child",
  "Is a foreign-origin spouse of an Indian citizen",
  "Is a foreign-origin spouse of an OCI cardholder",
];

const APPLY_STEPS = [
  "Confirm eligibility.", "Register through the official OCI portal.", "Complete the online application.",
  "Upload the required photograph, signature and documents.", "Pay the applicable government fee.",
  "Submit or present original documents when instructed.", "Attend the Indian Mission, Post or FRRO process if required.",
  "Track the application online.", "Verify all details after the OCI card or e-OCI is issued.",
];

const APPLY_DOCS = [
  "Current foreign passport", "Proof of present citizenship", "Naturalization or citizenship certificate",
  "Surrendered or cancelled Indian passport", "Indian passport surrender certificate, where applicable",
  "Birth certificate", "Marriage certificate", "Indian passport or OCI documents of a parent, grandparent or spouse",
  "Proof of relationship", "Domicile, nativity or other qualifying Indian-origin records",
  "Current address evidence", "Recent photograph and signature",
  "Long-duration Indian visa and residential permit when applying from India, where applicable",
];

const SPECIAL_PERMIT = [
  "Research", "Journalism", "Missionary work", "Tabligh activities", "Mountaineering",
  "Internships with foreign diplomatic missions", "Internships with foreign government organizations",
  "Employment with a foreign diplomatic mission", "Visits to Protected, Restricted or Prohibited Areas",
  "Other activities designated by the Government of India",
];

const CARRY = [
  "A valid foreign passport", "A valid physical OCI card or approved e-OCI documentation, as applicable",
  "A completed e-Arrival Card", "Airline itinerary", "Indian contact and accommodation details",
  "Supporting permission for restricted activities or places, if applicable",
  "Medication prescriptions and health documents when needed", "Documents required by any transit country",
];

const VERIFY_BEFORE = [
  "The name, date of birth and passport details are consistent.",
  "Required passport updates have been completed on the OCI portal.",
  "A replacement OCI card has been obtained when required.",
  "The OCI status has not been cancelled, renounced or placed under review.",
  "Any special permit required for the trip has been approved.",
  "The e-Arrival Card has been submitted through an official channel.",
];

const EARRIVAL_WHO = [
  "Foreign nationals traveling with an Indian visa or e-Visa", "OCI cardholders", "e-OCI cardholders",
  "Other covered foreign-passport travelers",
];

const EARRIVAL_INFO = [
  "Passport number", "Nationality", "Personal details", "Arrival date", "Flight or transportation details",
  "Port of arrival", "Purpose of visit", "Address in India", "Contact details",
  "Other immigration or travel declarations",
];

const EARRIVAL_NOT = [
  "Does not grant a visa", "Does not replace an e-Visa", "Does not replace a regular Indian visa",
  "Does not replace an OCI card", "Does not guarantee admission into India",
  "Does not replace a foreign passport", "Does not replace a required Protected or Restricted Area permit",
];

const CUSTOMS = [
  "Currency", "Gold and jewelry", "High-value goods", "Commercial merchandise",
  "Food, plants and animal products", "Medication", "Drones", "Satellite equipment",
  "Restricted or prohibited items", "Duty-free allowances",
];

const RISK = [
  "Registration obtained through fraud, false representation or concealment",
  "Conduct falling within statutory national-security or sovereignty grounds",
  "Serious criminal conviction within the period specified by law",
  "Violation of OCI conditions", "Changes affecting a spouse-based registration",
  "Voluntary renunciation", "Acquisition of Indian citizenship", "Other grounds authorized by law",
];

const LINKS = [
  ["OCI information, notifications and comparative guidance", "Ministry of Home Affairs OCI page", "https://www.mha.gov.in"],
  ["OCI registration and miscellaneous services", "OCI Services Portal", "https://ociservices.gov.in"],
  ["Apply for a new OCI", "Online OCI application", "https://ociservices.gov.in"],
  ["OCI eligibility, documents, photo specs, tracking and login", "OCI Services Portal — FAQ & services", "https://ociservices.gov.in"],
  ["India e-Arrival Card (free official form)", "Indian Visa Online — e-Arrival", "https://indianvisaonline.gov.in"],
  ["Indian visa, e-Visa, regular visa and Visa on Arrival", "India Visa Online", "https://indianvisaonline.gov.in"],
  ["Protected and Restricted Area permits", "MHA PAP/RAP information", "https://www.mha.gov.in"],
  ["Foreign-resident services in India", "FRRO e-Services", "https://indianfrro.gov.in/eservices/"],
  ["Aadhaar eligibility and enrollment for resident foreign nationals", "UIDAI", "https://uidai.gov.in"],
  ["Indian income-tax services", "Income Tax e-Filing Portal", "https://www.incometax.gov.in"],
];

const CHECKLIST = [
  "Check the validity of your foreign passport.", "Check your OCI or e-OCI status.",
  "Complete any required passport update through OCI Miscellaneous Services.",
  "Obtain a replacement OCI card if reissue is required.",
  "Confirm that personal information matches across documents.",
  "Complete the free e-Arrival Card within 72 hours before arrival.",
  "Save the e-Arrival acknowledgement.",
  "Obtain special permission for research, journalism, missionary work, mountaineering or other restricted activities.",
  "Obtain PAP/RAP authorization for protected or restricted destinations.",
  "Review airline baggage and transit-country requirements.",
  "Carry prescriptions for controlled medication.", "Review Indian Customs declaration rules.",
  "Keep copies of emergency contacts and accommodation details.",
  "Do not use unofficial sites that charge for the free e-Arrival Card.",
];

export default function IAOciNriArticle() {
  return (
    <article className="space-y-8" data-testid="india-oci-article">
      <header className="space-y-3">
        <span className="inline-block text-[10px] font-800 uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1">Updated September 2026</span>
        <h2 className="font-heading font-900 text-2xl md:text-3xl text-zinc-900">
          NRI and OCI Card Rules: Travel to India, Living in India, Benefits, Restrictions and Arrival Requirements
        </h2>
        <P>
          Millions of Indians and people of Indian origin live outside India while maintaining close family, cultural,
          professional and financial connections with the country. Two terms frequently used in this context are NRI and OCI,
          but they describe very different legal situations.
        </P>
        <P>
          An <b className="text-zinc-800">NRI</b> is ordinarily an Indian citizen living outside India. An{" "}
          <b className="text-zinc-800">OCI cardholder</b> is a foreign citizen who has received specified travel, residence
          and other privileges from the Government of India. Understanding this distinction is essential when traveling to
          India, living or working in India, buying property, opening accounts, studying, paying taxes or completing
          immigration formalities.
        </P>
        <Warn>
          <b>Important notice:</b> This article provides general information and is not immigration, citizenship, tax or legal
          advice. Government rules can change, and special rules may apply based on nationality, ancestry, occupation, travel
          purpose and place of residence. Always verify current requirements through the official Government of India portals
          linked below.
        </Warn>
      </header>

      <Section title="NRI and OCI are not the same">
        <div className="overflow-x-auto border border-zinc-200 rounded-2xl">
          <table className="w-full text-xs" data-testid="india-oci-compare-table">
            <thead>
              <tr className="bg-zinc-50 text-left">
                <th className="px-3.5 py-2.5 font-800 text-zinc-500 uppercase text-[10px]">Subject</th>
                <th className="px-3.5 py-2.5 font-800 text-blue-700 uppercase text-[10px]">NRI</th>
                <th className="px-3.5 py-2.5 font-800 text-orange-600 uppercase text-[10px]">OCI cardholder</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map(([s, n, o]) => (
                <tr key={s} className="border-t border-zinc-100 align-top">
                  <td className="px-3.5 py-2.5 font-700 text-zinc-800">{s}</td>
                  <td className="px-3.5 py-2.5 text-zinc-600">{n}</td>
                  <td className="px-3.5 py-2.5 text-zinc-600">{o}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Who is an NRI?">
        <P>
          "NRI" means Non-Resident Indian. In general usage, it describes an Indian citizen who ordinarily resides outside
          India. However, "non-resident" can be defined differently under different laws:
        </P>
        <Checks items={[
          "The Foreign Exchange Management Act considers residence, purpose of stay and intention.",
          "Income-tax law applies separate residence tests.",
          "Banking rules may classify accounts and investments according to FEMA status.",
          "Citizenship and passport status are separate from tax residency.",
        ]} />
        <P>
          A person may therefore be non-resident for one legal purpose while being treated differently for another. NRI status
          should never be determined solely from an informal day count without checking the applicable law.
        </P>
      </Section>

      <Section title="Travel rules for NRIs who remain Indian citizens">
        <P>An NRI who remains an Indian citizen normally travels to India using a valid Indian passport. Such a traveler:</P>
        <Checks items={NRI_TRAVEL} />
        <Warn>
          An NRI who has acquired another country's citizenship is no longer in the same position as an Indian citizen. India
          does not recognize dual citizenship in the conventional sense. A former Indian citizen should not continue using an
          Indian passport after acquiring foreign citizenship — complete the applicable Indian-passport surrender process and
          obtain an Indian visa or OCI registration before traveling.
        </Warn>
      </Section>

      <Section title="What is an OCI card?">
        <P>
          OCI means Overseas Citizen of India. Despite the name, an OCI cardholder is not an Indian citizen. OCI is a special
          immigration and registration status given to eligible foreign citizens under the Citizenship Act. The principal
          benefit is a multiple-entry, multipurpose, lifelong authorization to visit India, subject to the continued validity
          of OCI status and special-permission requirements for certain activities.
        </P>
        <H3>An OCI card is not:</H3>
        <Nots items={OCI_IS_NOT} />
        <P>Official information is available through the <Ext href="https://www.mha.gov.in">Ministry of Home Affairs OCI section</Ext> and the official <Ext href="https://ociservices.gov.in">OCI Services Portal</Ext>.</P>
      </Section>

      <Section title="Who may qualify for OCI registration?">
        <P>Eligibility can include a foreign citizen who:</P>
        <Checks items={ELIGIBLE} />
        <P>
          A spouse-based application generally requires the marriage to be registered and to have continued for at least two
          years immediately before the application. Security clearance and additional continuing-marriage requirements apply.
        </P>
        <Warn>
          A person is not eligible through the ordinary OCI ancestry route if the person — or specified parents, grandparents
          or great-grandparents — was a citizen of Pakistan, Bangladesh or another country notified by the Central Government.
          Check eligibility through the official <Ext href="https://ociservices.gov.in">OCI application portal</Ext>.
        </Warn>
      </Section>

      <Section title="Applying for an OCI card">
        <P>OCI applications are submitted online. Do not rely on unofficial agents or look-alike websites. The general process:</P>
        <ol className="space-y-1.5">
          {APPLY_STEPS.map((s, i) => (
            <li key={s} className="flex items-start gap-2.5 text-sm text-zinc-600">
              <span className="shrink-0 w-5 h-5 rounded-full bg-blue-700 text-white text-[10px] font-800 inline-flex items-center justify-center mt-0.5">{i + 1}</span>{s}
            </li>
          ))}
        </ol>
        <H3>Common supporting documents may include:</H3>
        <Checks items={APPLY_DOCS} />
        <Warn>
          Applications submitted in India have additional visa and residence conditions. Foreign nationals in India on tourist,
          missionary or mountaineering visas are not eligible to submit an OCI registration application from India under the
          general instructions.
        </Warn>
        <P>Official services — apply, check eligibility and documents, track your application, photo specifications and user login — are all on the <Ext href="https://ociservices.gov.in">OCI Services Portal</Ext>.</P>
      </Section>

      <Section title="Principal benefits of an OCI card">
        <H3>Lifelong multiple-entry travel authorization</H3>
        <P>An OCI cardholder generally does not need a new Indian visa for every visit — OCI provides a multiple-entry, multipurpose, lifelong visa authorization, subject to special-permission rules and the validity of the underlying OCI status.</P>
        <H3>Long-term residence in India</H3>
        <P>An OCI cardholder may generally live in India for an extended or indefinite period without repeated visa extensions. OCI is nevertheless not citizenship or permanent immunity from immigration action — the holder remains a foreign national and must comply with Indian laws and OCI conditions.</P>
        <H3>General exemption from routine FRRO registration</H3>
        <P>OCI cardholders are generally exempt from registering with a Foreigners Regional Registration Officer merely because of the length of their stay. However, an OCI cardholder who becomes normally resident in India must follow the applicable reporting requirements for changes in permanent residential address or occupation — see <Ext href="https://indianfrro.gov.in/eservices/">FRRO e-Services</Ext>.</P>
        <H3>Ability to work in many private-sector occupations</H3>
        <Checks items={[
          "Regulated professions require the appropriate Indian qualification, registration or professional license.",
          "Government posts may be restricted to Indian citizens.",
          "Employment with foreign diplomatic missions or foreign government organizations can require special permission.",
          "Journalism, research and certain other activities require separate authorization.",
          "Employment does not automatically establish a particular income-tax or FEMA status.",
        ]} />
        <H3>Business and investment opportunities</H3>
        <P>OCI cardholders may generally establish or participate in eligible businesses and investments subject to FEMA rules, foreign-direct-investment rules, sectoral restrictions, company and tax law, banking and repatriation requirements, beneficial-ownership reporting and licensing. OCI status does not override restrictions applicable to foreign investment.</P>
        <H3>Residential and commercial property</H3>
        <P>OCI cardholders generally receive treatment comparable to NRIs for acquiring eligible residential and commercial immovable property. They cannot ordinarily purchase agricultural land, plantation property or farmhouses. Inheritance and transfer of restricted property are governed by separate rules — obtain legal advice and verify the property's classification in official land records before making any payment.</P>
        <H3>Educational opportunities</H3>
        <P>OCI cardholders may receive specified treatment comparable to NRIs for admissions and may be eligible for certain examinations or admission categories. This does not necessarily provide access to citizen-only seats, every government-subsidized fee, every quota, scholarship or professional license — verify with the institution and examination authority.</P>
        <H3>Specified professional opportunities</H3>
        <P>OCI notifications provide access, subject to the relevant laws and professional regulators, to certain specified professions — designated medical, dental, nursing, pharmaceutical, legal, architectural, accountancy and teaching roles. An OCI card alone is not a professional license.</P>
      </Section>

      <Section title="Important restrictions OCI cardholders must watch">
        <Checks items={[
          "OCI is not Indian citizenship — the foreign passport is the primary passport and must remain valid.",
          "No voting rights, and no right to contest elections, sit in Parliament or a State Legislature, or hold specified constitutional offices.",
          "Not generally entitled to public employment reserved for Indian citizens under Article 16 of the Constitution.",
          "Cannot ordinarily purchase agricultural land, plantation property or a farmhouse — check the land records, not the marketing brochure.",
          "Cannot use or obtain an Indian passport unless the person is an Indian citizen.",
          "Indian civil and criminal laws fully apply — no immunity from tax, police powers, courts, employment, property, foreign-exchange, licensing, customs or immigration enforcement.",
        ]} />
        <H3>Activities requiring prior permission or a special permit</H3>
        <Checks items={SPECIAL_PERMIT} />
        <Warn>
          Permission may need to be obtained from an Indian Mission, the Ministry of Home Affairs, the FRRO/FRO or another
          competent authority. Travelers to sensitive border, island, military or tribal regions should check the official{" "}
          <Ext href="https://www.mha.gov.in">Protected Area Permit and Restricted Area Permit information</Ext>. Do not assume
          an OCI card eliminates PAP, RAP or special-permission requirements.
        </Warn>
      </Section>

      <Section title="Traveling to India with an OCI card">
        <H3>An OCI traveler should carry:</H3>
        <Checks items={CARRY} />
        <H3>Before departure, verify that:</H3>
        <Checks items={VERIFY_BEFORE} />
        <Warn>An airline may deny boarding when travel documentation is incomplete or inconsistent, even if the traveler believes the underlying OCI status remains valid.</Warn>
      </Section>

      <Section title="OCI card reissue and passport-update rules">
        <P>An OCI card does not ordinarily need to be reissued every time an adult renews a passport — but important update rules apply.</P>
        <H3>OCI card issued before age 20</H3>
        <P>The OCI card must generally be reissued once when a new passport is issued after the holder completes 20 years of age, so the card reflects the person's adult facial features.</P>
        <H3>Passport updates up to age 20</H3>
        <P>Each time a new passport is issued up to age 20, upload a copy of the new passport (photo page) and a recent photograph through OCI Miscellaneous Services — generally within three months of receiving the new passport.</P>
        <H3>Update after age 50</H3>
        <P>Upload the new passport and a recent photograph once after completing 50 years of age, per the applicable OCI update instructions.</P>
        <H3>Spouse-based OCI registration</H3>
        <P>When a new passport is issued, a spouse-based OCI holder may need to upload the new passport, a recent photograph, and a declaration from both spouses confirming that the marriage continues. Divorce, dissolution, death of the spouse, separation or remarriage can materially affect spouse-based OCI status and should be reported and reviewed promptly.</P>
        <H3>Changes in personal details</H3>
        <P>Use OCI Miscellaneous Services for changes involving name, nationality, passport details, photograph, address, occupation, marital information or lost/damaged OCI documentation — instructions, fees and evidence requirements are in the <Ext href="https://ociservices.gov.in">OCI Miscellaneous Services FAQ</Ext>.</P>
        <H3>Lost or damaged OCI card</H3>
        <ol className="space-y-1.5">
          {["Report the loss or theft to the local police.", "Obtain a copy of the police report.",
            "Arrange an authorized translation if the report is not in English or another accepted language.",
            "Apply through OCI Miscellaneous Services.", "Upload the available copy of the OCI card and required passport documents.",
            "Follow instructions from the Indian Mission, Post or FRRO.",
            "Do not travel until the airline and Indian authorities confirm that the available documents are sufficient."].map((s, i) => (
            <li key={s} className="flex items-start gap-2.5 text-sm text-zinc-600">
              <span className="shrink-0 w-5 h-5 rounded-full bg-blue-700 text-white text-[10px] font-800 inline-flex items-center justify-center mt-0.5">{i + 1}</span>{s}
            </li>
          ))}
        </ol>
      </Section>

      <Section title="Living in India as an OCI cardholder">
        <H3>Address and occupation reporting</H3>
        <P>An OCI cardholder normally resident in India should notify the jurisdictional FRRO/FRO when there is a change in permanent residential address or occupation. Keep records of submitted notifications and acknowledgements.</P>
        <H3>PAN and income tax</H3>
        <P>A Permanent Account Number may be needed when earning income, investing, buying property, conducting business or completing specified financial transactions. OCI status does not determine tax residency — tax liability depends on physical presence, residential status under tax law, source and type of income, applicable tax treaty and reporting rules. Indian-source salary, rent, capital gains, business and investment income may be taxable in India even when the person remains tax-resident elsewhere. Official services: <Ext href="https://www.incometax.gov.in">Income Tax e-Filing Portal</Ext>.</P>
        <H3>Banking</H3>
        <P>Bank-account classification depends on FEMA residence, not merely possession of an OCI card. Depending on status and circumstances, appropriate accounts may include NRE accounts, NRO accounts, FCNR(B) deposits or resident accounts after a change in residential status. Notify banks and redesignate accounts when moving to or leaving India.</P>
        <H3>Aadhaar</H3>
        <P>An OCI card does not automatically provide an Aadhaar number. UIDAI states that a resident foreign national, including an OCI cardholder, may generally enroll after residing in India for at least 182 days during the 12 months immediately preceding the application, using the designated resident-foreigner enrollment form (Form 8) with valid identity and Indian-address documents. Aadhaar is an identity number; it is not proof of Indian citizenship. See <Ext href="https://uidai.gov.in">UIDAI enrollment guidance</Ext>.</P>
        <H3>Driving, healthcare and insurance</H3>
        <P>An OCI resident may be able to obtain a driving license, local insurance, private healthcare and utility connections, subject to the same documentary and state-specific requirements applied to eligible foreign residents. OCI status does not automatically guarantee free public healthcare, government welfare benefits, subsidized services, a driving license, insurance eligibility or citizen-only benefits.</P>
      </Section>

      <Section title="India e-Arrival Card requirements">
        <P>
          India provides an official e-Arrival Card system for foreign nationals and OCI/e-OCI cardholders arriving in India.
          The Government of India's official visa portal states that these travelers must complete and submit the e-Arrival
          Card online <b className="text-zinc-800">within 72 hours before arrival</b> in India.
        </P>
        <H3>Who must complete it?</H3>
        <Checks items={EARRIVAL_WHO} />
        <P>An NRI who remains an Indian citizen and travels on an Indian passport is not an OCI/foreign-national traveler for this purpose.</P>
        <Warn>
          Complete the form only through the Government of India's official portal — <Ext href="https://indianvisaonline.gov.in">India e-Arrival Card, official form</Ext>. The service is <b>free</b>; the Government of India has not authorized agents or intermediaries to charge fees. It may also be completed through the official Indian Visa Su-Swagatam mobile application.
        </Warn>
        <H3>Information travelers should prepare</H3>
        <Checks items={EARRIVAL_INFO} />
        <P>Enter every detail exactly as shown on the passport and travel documents.</P>
        <H3>The e-Arrival Card is not a visa</H3>
        <Nots items={EARRIVAL_NOT} />
        <P>The traveler must separately hold valid authorization to enter India — see <Ext href="https://indianvisaonline.gov.in">India Visa Online</Ext>.</P>
        <H3>After submitting the e-Arrival Card</H3>
        <Checks items={[
          "Review the submitted information carefully.", "Save the confirmation or acknowledgement.",
          "Keep a digital copy accessible offline.", "Carry a printed copy if desired or if instructed.",
          "Keep the Indian address and local contact readily available.",
          "Present information to airline or immigration officials when requested.",
        ]} />
        <P>Every traveler should have an individual submission unless the official form expressly provides otherwise. Parents or guardians should complete the required information for accompanying children.</P>
        <H3>Arrival card versus customs declaration</H3>
        <P>The e-Arrival Card is an immigration-arrival document — it does not eliminate customs obligations. Travelers must separately comply with Indian customs rules concerning:</P>
        <Checks items={CUSTOMS} />
        <H3>Visa on Arrival is a separate program</H3>
        <P>India's Visa on Arrival program is different from the e-Arrival Card — it is available only to specified eligible nationalities under limited conditions at designated airports. Completing an e-Arrival Card does not make a traveler eligible for Visa on Arrival.</P>
      </Section>

      <Section title="When OCI status can be placed at risk">
        <P>OCI registration may be cancelled or affected in circumstances prescribed by the Citizenship Act and applicable rules, including:</P>
        <Checks items={RISK} />
        <Warn>An OCI cardholder facing arrest, prosecution, a major status change, divorce in a spouse-based case or a notice from the government should obtain qualified legal advice immediately.</Warn>
      </Section>

      <Section title="Essential official links and forms">
        <div className="overflow-x-auto border border-zinc-200 rounded-2xl">
          <table className="w-full text-xs" data-testid="india-oci-links-table">
            <thead>
              <tr className="bg-zinc-50 text-left">
                <th className="px-3.5 py-2.5 font-800 text-zinc-500 uppercase text-[10px]">Purpose</th>
                <th className="px-3.5 py-2.5 font-800 text-zinc-500 uppercase text-[10px]">Official Government of India resource</th>
              </tr>
            </thead>
            <tbody>
              {LINKS.map(([p, label, href]) => (
                <tr key={p} className="border-t border-zinc-100 align-top">
                  <td className="px-3.5 py-2.5 text-zinc-600">{p}</td>
                  <td className="px-3.5 py-2.5"><Ext href={href}>{label}</Ext></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Pre-travel checklist for OCI cardholders">
        <ol className="space-y-1.5" data-testid="india-oci-checklist">
          {CHECKLIST.map((s, i) => (
            <li key={s} className="flex items-start gap-2.5 text-sm text-zinc-600">
              <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-600 text-white text-[10px] font-800 inline-flex items-center justify-center mt-0.5">{i + 1}</span>{s}
            </li>
          ))}
        </ol>
      </Section>

      <Section title="Final reminder">
        <P>
          The OCI card is one of India's most valuable programs for the global Indian community. It allows eligible foreign
          citizens of Indian origin and qualifying spouses and children to visit India repeatedly, maintain long-term
          residence, work in many private-sector roles, study, invest and preserve meaningful connections with India.
        </P>
        <P>
          But OCI is not citizenship. The holder must continue using a valid foreign passport, comply with Indian laws,
          observe property and political restrictions, obtain permission for regulated activities, update passport information
          when required and complete the e-Arrival Card before travel. NRIs who remain Indian citizens have a different legal
          position: they travel on Indian passports and retain the rights and responsibilities of Indian citizenship, while
          their banking, tax and investment treatment may depend on their non-resident status.
        </P>
        <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white rounded-2xl p-5 text-center" data-testid="india-oci-closing">
          <p className="text-sm font-800">The safest approach is simple</p>
          <p className="mt-1 text-xs text-emerald-100">Identify whether you are traveling as an Indian citizen or a foreign citizen, use only official government portals, keep your documents updated and verify special requirements before every journey.</p>
        </div>
      </Section>
    </article>
  );
}
